import React from "react";
import {Link} from "react-router-dom";
import ReactTable from "react-table";
import {Button} from "reactstrap";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import moment from "moment";

import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";

import "./Memberships.scss";

const toastOptions = {
	position: API.Toast.POSITION && API.Toast.POSITION.TOP_CENTER ? API.Toast.POSITION.TOP_CENTER : "top-center",
};

const breadcrumbsStatic = [
	{
		name: "Memberships",
		url: "/memberships",
		icon: "id-card-o",
	},
	{
		name: "History",
		url: "/memberships/history",
		icon: "history",
	},
];

const billingStatusFilters = [
	{pkey: "", name: "All History"},
	{pkey: "success", name: "Successful Bills"},
	{pkey: "failed", name: "Failed Bills"},
];

const intValue = (value) => {
	const parsedValue = parseInt(value, 10);
	return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const isBaseMembership = (membership) => !!(
	membership
	&& !intValue(membership.customer || membership.Customer)
	&& !intValue(membership.membership_plan || membership.MembershipPlan)
);

const isBaseMembershipHistoryRow = (historyRow) => !!(
	historyRow
	&& historyRow.membership
	&& !historyRow.membership_plan
	&& !historyRow.customer
);

const historyFilterValue = (urlParams, name) => {
	const value = urlParams.get(name);
	return value ? value.trim() : "";
};

const initialHistoryFilters = () => {
	const urlParams = new URLSearchParams(window.location.search);

	return {
		customer: historyFilterValue(urlParams, "customer"),
		eventType: historyFilterValue(urlParams, "event_type"),
		expandFirst: historyFilterValue(urlParams, "expand") === "1",
		membership: historyFilterValue(urlParams, "membership"),
		search: historyFilterValue(urlParams, "search"),
		status: historyFilterValue(urlParams, "status"),
	};
};

const clearHistoryFilterQueryString = () => {
	if (window.history && window.history.replaceState) {
		window.history.replaceState(null, "", window.location.pathname);
	}
};

const formatDateTime = (value) => value ? moment(value).local().format("MM/DD/YYYY h:mm A") : "-";

const formatCurrency = (value) => {
	const amount = parseFloat(value);
	return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "-";
};

const isMembershipApiKeyConfigurationError = (historyRow) => {
	const membershipHistoryEvent = historyRow.original || historyRow;

	return [membershipHistoryEvent.response_message, membershipHistoryEvent.summary].some((message) => (
		typeof message === "string"
		&& message.indexOf("Membership API Key is not configured or invalid") !== -1
	));
};

const formatHistoryDate = (value) => {
	if (!value || value === "none") {
		return "";
	}

	const date = moment(value, "YYYY-MM-DD", true);
	return date.isValid() ? date.format("MM/DD/YYYY") : value;
};

const membershipChangeFieldLabels = {
	billing_day: "Billing Day",
	billing_period: "Billing Period",
	card: "Card",
	card_brand: "Card Brand",
	card_last_four: "Card Last Four",
	cancelled_at: "Cancelled At",
	cost_per_month: "Billing Amount",
	customer: "Customer",
	discount_percentage: "Discount Percentage",
	end_date: "End Date",
	failed_billing_count: "Failed Billing Count",
	free_range_minutes: "Free Range Time",
	inventory_group_discounts: "Inventory Group Discounts",
	ipospays_reference_id: "iPOSpays Reference",
	ipospays_subscription_id: "iPOSpays Subscription",
	last_bill_date: "Last Bill Date",
	last_billing_message: "Last Billing Message",
	last_billing_status: "Last Billing Status",
	membership_plan: "Membership Plan",
	name: "Name",
	next_bill_date: "Next Bill Date",
	notes: "Notes",
	payment_processor: "Payment Processor",
	start_date: "Start Date",
	status: "Status",
	terminal: "Terminal",
	tokenized_at: "Tokenized At",
};

const titleCaseMembershipField = (field) => field
	.split("_")
	.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
	.join(" ");

const formatEmptyHistoryValue = (value) => {
	if (value === null || typeof value === "undefined" || value === "" || value === "0") {
		return "Not set";
	}

	return value;
};

const formatMembershipChangeValue = (field, value) => {
	if (field === "cost_per_month") {
		return formatCurrency(value);
	}
	if (field === "billing_period") {
		return value === "yearly" ? "Yearly" : "Monthly";
	}
	if (field === "discount_percentage") {
		const percentage = parseFloat(value);
		return Number.isFinite(percentage) ? `${percentage.toFixed(2)}%` : "Not set";
	}
	if (field === "free_range_minutes") {
		const minutes = parseInt(value, 10);
		return Number.isFinite(minutes) ? `${minutes} min` : "Not set";
	}
	if (field === "billing_day") {
		const billingDay = parseInt(value, 10);
		return Number.isFinite(billingDay) && billingDay > 0 ? `Day ${billingDay}` : "Not set";
	}
	if (field === "failed_billing_count") {
		const failedBillingCount = parseInt(value, 10);
		return Number.isFinite(failedBillingCount) ? `${failedBillingCount}` : "0";
	}
	if ([
		"cancelled_at",
		"end_date",
		"last_bill_date",
		"next_bill_date",
		"start_date",
		"tokenized_at",
	].indexOf(field) !== -1) {
		return formatHistoryDate(value) || "Not set";
	}

	return `${formatEmptyHistoryValue(value)}`;
};

const parseRawMembershipSummary = (summary) => {
	if (!summary || summary.indexOf("name=") === -1 || summary.indexOf(";") === -1) {
		return null;
	}

	const planMatch = summary.match(/^Plan #(\d+):\s*(.*)$/);
	const summaryBody = planMatch ? planMatch[2] : summary;
	const details = summaryBody.split(";").reduce((parsedDetails, detail) => {
		const separatorIndex = detail.indexOf("=");
		if (separatorIndex === -1) {
			return parsedDetails;
		}

		const key = detail.slice(0, separatorIndex).trim();
		const value = detail.slice(separatorIndex + 1).trim();
		parsedDetails[key] = value;
		return parsedDetails;
	}, {});

	if (!details.name) {
		return null;
	}

	const meta = [
		details.cost,
		details.disc ? `${details.disc} discount` : "",
		details.range ? `${details.range.replace(/m$/, "")} range minutes` : "",
		details.bill_day && details.bill_day !== "none" ? `bills on day ${details.bill_day}` : "",
		formatHistoryDate(details.start) ? `starts ${formatHistoryDate(details.start)}` : "",
		formatHistoryDate(details.next) ? `next bill ${formatHistoryDate(details.next)}` : "",
	].filter(Boolean);
	const support = [
		details.card && details.card !== "No card on file" ? `Card: ${details.card}` : "",
		details.groups && details.groups !== "None" ? `Discount groups: ${details.groups}` : "",
	].filter(Boolean);

	return {
		plan: planMatch ? `Plan #${planMatch[1]}` : "",
		name: details.name,
		meta,
		support,
	};
};

const statusClassName = (status) => {
	switch ((status || "").toLowerCase()) {
		case "success":
			return "success";
		case "failed":
		case "error":
			return "failed";
		case "pending":
			return "pending";
		default:
			return "neutral";
	}
};

export default class MembershipHistory extends React.Component {
	constructor(props) {
		super(props);
		this.model = new API.BuildClass(Types.Memberships._name, "GET_HISTORY");
		const filters = initialHistoryFilters();
		this.state = {
			data: [],
			pages: 0,
			page: 0,
			limit: 50,
			loading: false,
			search: filters.search,
			appliedSearch: filters.search,
			customerFilter: filters.customer,
			eventTypeFilter: filters.eventType,
			expanded: filters.expandFirst ? {0: true} : {},
			key: Math.random(),
			membershipFilter: filters.membership,
			statusFilter: filters.status,
		};
		this.tableState = null;
	}

	isHistoryRowExpanded = (tableState, rowInfo) => {
		if (!rowInfo || !rowInfo.nestingPath || !tableState || !tableState.expanded) {
			return false;
		}

		return !!rowInfo.nestingPath.reduce((expandedPath, pathPart) => (
			expandedPath && expandedPath[pathPart] ? expandedPath[pathPart] : null
		), tableState.expanded);
	};

	getHistoryTrProps = (tableState, rowInfo) => ({
		className: this.isHistoryRowExpanded(tableState, rowInfo) ? "membership-history-row-expanded" : "",
	});

	fetchHistory = async (tableState = this.tableState || {}) => {
		this.tableState = tableState;
		const sorted = tableState.sorted && tableState.sorted.length ? tableState.sorted[0] : null;
		const page = typeof tableState.page === "number" ? tableState.page : this.state.page;
		const limit = tableState.pageSize || this.state.limit;

		this.setState({
			loading: true,
			page,
			limit,
		});

		try {
			const requestType = "GET_HISTORY";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				history_customer: this.state.customerFilter,
				history_event_type: this.state.eventTypeFilter,
				history_page: page,
				history_limit: limit,
				history_membership: this.state.membershipFilter,
				history_search: this.state.appliedSearch,
				history_status: this.state.statusFilter,
				history_sort: sorted ? sorted.id : "created",
				history_order: sorted && sorted.desc === false ? "asc" : "desc",
			};
			const historyResponse = await requestModel.submit(null, requestType, true) || {};
			this.setState({
				data: historyResponse.items || [],
				pages: historyResponse.pages || 0,
				page: historyResponse.page || page,
				limit: historyResponse.limit || limit,
				loading: false,
			});
		} catch (error) {
			this.setState({loading: false});
			API.Toast.error(error.message || "Unable to load membership history.", toastOptions);
		}
	};

	refreshHistory = () => {
		this.fetchHistory(this.tableState || {
			page: this.state.page,
			pageSize: this.state.limit,
			sorted: [],
			filtered: [],
		});
	};

	updateSearch = (name, search) => {
		this.setState({search});
	};

	handleSearchKeyDown = (event) => {
		if (event.key === "Enter") {
			this.applySearch();
		}
	};

	applySearch = () => {
		this.setState({
			appliedSearch: this.state.search.trim(),
			key: Math.random(),
		});
	};

	updateBillingStatusFilter = (name, statusFilter) => {
		this.setState({
			eventTypeFilter: statusFilter ? `membership_billing_${statusFilter}` : "",
			page: 0,
			statusFilter,
			key: Math.random(),
		});
	};

	updateMembershipFilter = (name, pkey) => {
		this.setState({
			membershipFilter: pkey || "",
			page: 0,
			key: Math.random(),
		});
	};

	updateCustomerFilter = (name, pkey) => {
		this.setState({
			customerFilter: pkey || "",
			page: 0,
			key: Math.random(),
		});
	};

	clearSearch = () => {
		clearHistoryFilterQueryString();
		this.setState({
			search: "",
			appliedSearch: "",
			customerFilter: "",
			eventTypeFilter: "",
			expanded: {},
			key: Math.random(),
			membershipFilter: "",
			statusFilter: "",
		});
	};

	renderHistoryRefresh = () => {
		const label = "Refresh Membership History";

		return (
			<Button
				type="button"
				size="sm"
				outline
				className="membership-table-refresh membership-history-refresh secondary-button"
				disabled={this.state.loading}
				onClick={this.refreshHistory}
				title={label}
				aria-label={label}
			>
				<i className={`fa fa-refresh${this.state.loading ? " fa-spin" : ""}`}/>
			</Button>
		);
	};

	renderMembership = (historyRow) => {
		if (!historyRow.membership) {
			return "-";
		}

		const label = historyRow.membership_name || `Deleted membership #${historyRow.membership}`;
		if (!historyRow.membership_name) {
			return label;
		}

		return (
			<Link to={`/memberships/${historyRow.membership}`}>
				{label}
			</Link>
		);
	};

	renderCustomer = (historyRow) => {
		if (!historyRow.customer) {
			return "-";
		}

		return (
			<Link to={`/customers/${historyRow.customer}`}>
				{historyRow.customer_name || `Customer #${historyRow.customer}`}
			</Link>
		);
	};

	renderResponse = (historyRow) => {
		const responseParts = [
			historyRow.response_code,
			historyRow.response_message,
		].filter(Boolean);

		return responseParts.length ? responseParts.join(" - ") : "-";
	};

	renderSummary = (summary) => {
		const membershipSummary = parseRawMembershipSummary(summary);
		if (!membershipSummary) {
			return <span>{summary || "-"}</span>;
		}

		return (
			<span className="membership-history-summary">
				<span className="membership-history-summary-title">
					{membershipSummary.plan ? `${membershipSummary.plan}: ` : ""}{membershipSummary.name}
				</span>
				{membershipSummary.meta.length ?
					<span>{membershipSummary.meta.join(" | ")}</span>
					: null}
				{membershipSummary.support.length ?
					<span>{membershipSummary.support.join(" | ")}</span>
					: null}
			</span>
		);
	};

	renderEvent = (historyRow) => (
		<div className={`membership-history-event ${isBaseMembershipHistoryRow(historyRow) ? "membership-history-event-base" : ""}`}>
			<strong>{historyRow.event_label || historyRow.event_type}</strong>
			{this.renderSummary(historyRow.summary)}
		</div>
	);

	renderChanges = (historyRow) => {
		const details = historyRow.original && historyRow.original.details ? historyRow.original.details : {};
		const changes = details && details.changes && typeof details.changes === "object" ? details.changes : {};
		const changeFields = Object.keys(changes).filter((field) => (
			changes[field]
			&& Object.prototype.hasOwnProperty.call(changes[field], "from")
			&& Object.prototype.hasOwnProperty.call(changes[field], "to")
		));

		if (!changeFields.length) {
			return null;
		}

		return (
			<div className="membership-history-changes">
				<div className="membership-history-changes-heading">
					<span>Updated Values</span>
				</div>
				<div className="membership-history-change-rows">
					{changeFields.map((field) => (
						<div className="membership-history-change-row" key={`membership-history-change-${field}`}>
							<strong>{membershipChangeFieldLabels[field] || titleCaseMembershipField(field)}</strong>
							<div>
								<span>Previous</span>
								<b>{formatMembershipChangeValue(field, changes[field].from)}</b>
							</div>
							<div>
								<span>New</span>
								<b>{formatMembershipChangeValue(field, changes[field].to)}</b>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	renderCancellationDetails = (historyRow) => {
		const details = historyRow.original && historyRow.original.details ? historyRow.original.details : {};
		if (!details.cancellation_reason) {
			return null;
		}

		return (
			<div className="membership-history-cancellation">
				<strong>Cancellation Reason</strong>
				<span>{details.cancellation_reason}</span>
				{details.cancelled_at ?
					<span className="membership-history-cancellation-date">Cancelled {formatDateTime(details.cancelled_at)}</span>
					: null}
			</div>
		);
	};

	renderExpandedHistory = (historyRow) => {
		return (
			<div className="membership-history-details">
				{isMembershipApiKeyConfigurationError(historyRow) ?
					<div className="membership-history-integration-fix">
						<span>The Membership API key needs to be configured before billing can be retried.</span>
						<Link className="btn btn-primary btn-sm" to="/settings#integrations">
							<i className="fa fa-plug"/>&nbsp;&nbsp;Fix in Integrations
						</Link>
					</div>
					: null}
				{this.renderCancellationDetails(historyRow)}
				{this.renderChanges(historyRow)}
				<div className="membership-history-detail-grid">
					<div>
						<span>Payment Processor</span>
						<strong>{historyRow.original.payment_processor || "-"}</strong>
					</div>
					<div>
						<span>Reference</span>
						<strong>{historyRow.original.ipospays_reference_id || "-"}</strong>
					</div>
					<div>
						<span>Transaction</span>
						<strong>{historyRow.original.ipospays_transaction_id || "-"}</strong>
					</div>
					<div>
						<span>Terminal</span>
						<strong>{historyRow.original.terminal_name || "-"}</strong>
					</div>
					<div>
						<span>Card</span>
						<strong>{historyRow.original.card_label || "-"}</strong>
					</div>
					<div>
						<span>User</span>
						<strong>{historyRow.original.user_name || "-"}</strong>
					</div>
				</div>
			</div>
		);
	};

	render() {
		const {data, pages, limit, loading} = this.state;
		const {properties} = this.model;
		const columns = [
			{
				Header: "",
				expander: true,
				width: 36,
				Expander: ({isExpanded}) => (
					<span className="membership-history-expander">
						<i className={`fa ${isExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}/>
					</span>
				),
				style: {cursor: "pointer", textAlign: "center"},
			},
			{
				Header: "Date",
				id: "created",
				accessor: row => formatDateTime(row.created),
				width: 155,
			},
			{
				Header: "Event",
				id: "event_label",
				accessor: row => row.event_label,
				Cell: props => this.renderEvent(props.original),
				className: "membership-history-event-cell",
				minWidth: 260,
			},
			{
				Header: "Status",
				id: "status",
				accessor: row => row.status || "success",
				width: 100,
				Cell: props => (
					<span className={`membership-history-status ${statusClassName(props.value)}`}>
						{props.value}
					</span>
				),
			},
			{
				Header: "Membership",
				id: "membership_name",
				accessor: row => row.membership_name || row.membership,
				Cell: props => this.renderMembership(props.original),
				minWidth: 170,
			},
			{
				Header: "Customer",
				id: "customer_name",
				accessor: row => row.customer_name || row.customer,
				Cell: props => this.renderCustomer(props.original),
				minWidth: 150,
			},
			{
				Header: "Amount",
				id: "amount",
				accessor: row => formatCurrency(row.amount),
				width: 90,
			},
			{
				Header: "Card",
				id: "card",
				accessor: row => row.card_label || "-",
				width: 130,
			},
			{
				Header: "Response",
				id: "response_message",
				accessor: this.renderResponse,
				minWidth: 190,
			},
			{
				Header: "User",
				id: "user_name",
				accessor: row => row.user_name || "Account Owner",
				minWidth: 130,
			},
		];

		return (
			<div className="container-fluid memberships-page">
				<Framework.Components.PageBar breadcrumbs={breadcrumbsStatic} history={this.props.history}/>

				<div className="memberships-header">
					<div>
						<h3>Membership History</h3>
						<span>Membership plans, customer memberships, cards, billing, and settings</span>
					</div>
					<div className="memberships-header-badge">
						<i className="fa fa-history"/>
						<span>Audit Trail</span>
					</div>
				</div>

				<Card className="memberships-table-card membership-history-card">
					<CardContent>
						<div className="table-bar memberships-table-bar membership-history-bar">
							<div className="membership-history-search">
								<div className="membership-history-text-filter">
									<Framework.Elements.TextField
										value={this.state.search}
										error=""
										property={properties.history_search}
										placeholder="Search history"
										update={this.updateSearch}
										onKeyDown={this.handleSearchKeyDown}
									/>
								</div>
								<Button type="button" className="primary-button" onClick={this.applySearch}>
									<i className="fa fa-search"/>&nbsp;&nbsp;Search
								</Button>
								<Button type="button" className="secondary-button" outline onClick={this.clearSearch}>
									<i className="fa fa-times"/>&nbsp;&nbsp;Clear
								</Button>
								<div className="membership-history-billing-status-filter">
									<Framework.Elements.Select
										shouldLoad={true}
										value={this.state.statusFilter}
										error=""
										objects={billingStatusFilters}
										property={properties.history_status}
										update={this.updateBillingStatusFilter}
										text="name"
										limit={0}
									/>
								</div>
								<div className="membership-history-entity-filter">
									<Framework.Elements.Select
										shouldLoad={true}
										value={this.state.membershipFilter}
										error=""
										property={properties.history_membership}
										label=""
										update={this.updateMembershipFilter}
										text="name"
										sort="name"
										nullable={true}
										default={{value: "", label: "All Memberships"}}
										filter={isBaseMembership}
										limit={20}
									/>
								</div>
								<div className="membership-history-entity-filter">
									<Framework.Elements.Select
										shouldLoad={true}
										value={this.state.customerFilter}
										error=""
										property={properties.history_customer}
										label=""
										update={this.updateCustomerFilter}
										text="name"
										sort="name"
										nullable={true}
										default={{value: "", label: "All Customers"}}
										limit={20}
									/>
								</div>
							</div>
							{this.renderHistoryRefresh()}
						</div>

						<ReactTable
							key={this.state.key}
							columns={columns}
							manual
							data={data}
							pages={pages}
							loading={loading}
							loadingText="Requesting Data"
							noDataText="No membership history found"
							onFetchData={this.fetchHistory}
							page={this.state.page}
							onPageChange={(page) => this.setState({page})}
							onPageSizeChange={(pageSize) => this.setState({limit: pageSize})}
							expanded={this.state.expanded}
							onExpandedChange={(expanded) => this.setState({expanded})}
							expanderDefaults={{
								sortable: false,
								resizable: false,
								filterable: false,
								width: 36,
							}}
							defaultSorted={[
								{
									id: "created",
									desc: true,
								},
							]}
							showPagination
							showPaginationTop={false}
							showPaginationBottom
							showPageSizeOptions
							defaultPageSize={limit}
							pageSize={limit}
							pageSizeOptions={[20, 50, 100, 250, 500]}
							className="-striped -highlight memberships-table membership-history-table"
							getTrProps={this.getHistoryTrProps}
							SubComponent={this.renderExpandedHistory}
						/>

						<Framework.Elements.LoadingSpinner isLoading={loading}/>
					</CardContent>
				</Card>
			</div>
		);
	}
}
